/**
 * What the relay last said about the inbox being readable, so the page stops asking a question whose answer
 * cannot have changed.
 *
 * The relay answers its two reads `{available: false, reason}` when there is no inbox. With `not_configured`
 * this installation does not use Projects, which only a new deployment changes: nothing is asked again while
 * the page lives. With `projects_unavailable` the count is asked again only after `RETRY`, whatever the header
 * entry's own interval, while a page of the inbox (opening the panel or the inbox) still asks. A readable
 * answer clears either.
 */
export class Availability {
	/** How long an unavailable count stands before it is asked again while Projects does not answer */
	static RETRY = 300000;

	#reason: string | null = null;
	#until = 0;

	/** Why the inbox was last unavailable, or null while it is readable */
	get reason(): string | null {
		return this.#reason;
	}

	/**
	 * Whether the last unavailable answer still stands for this read, so no request is sent
	 *
	 * @param count A count (the header's periodic read) rather than a page of the inbox
	 */
	held(count: boolean): boolean {
		if (this.#reason === null) return false;
		return this.#reason === 'not_configured' || (count && Date.now() < this.#until);
	}

	/**
	 * Records an answer of a read
	 *
	 * @returns Whether the answer says the inbox is unavailable
	 */
	note(answer: unknown): boolean {
		const document = answer as { available?: unknown; reason?: unknown } | null | undefined;
		if (document?.available !== false) {
			this.#reason = null;
			return false;
		}
		this.#reason = typeof document.reason === 'string' ? document.reason : 'projects_unavailable';
		this.#until = Date.now() + Availability.RETRY;
		return true;
	}
}
